import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { orderTrackingModule } from '../../../api/modules/orderTracking.module';
import { showToast } from '../../../utils/toast';
import { useAuth } from '../../../hooks/useAuth';
import { isSettled } from './tracking.status';

/**
 * Everything the two order-tracking routes DO, in one hook.
 *
 * The view next door is pure and the two containers are five lines each, so
 * this is where the lookup mutation, the rating mutation, the dialog state and
 * the buyer gate live. It is a hook rather than a third container because the
 * public and admin routes want identical behaviour with a different mutation
 * key, and a hook is the smallest thing that expresses "identical except for
 * this one string".
 *
 * BEHAVIOUR PRESERVED FROM BOTH PRE-CONVERSION PAGES
 * --------------------------------------------------
 * - The same GET, through the same `orderTrackingModule.trackingOrder`.
 * - The same `location.state.trackingId` prefill, so arriving from an order row
 *   lands with the field already filled.
 * - The same empty-input guard.
 * - The same review submission through `orderTrackingModule.submitRating`,
 *   which `ReviewForm` used to own privately.
 * - The same buyer gate on the actions, `shipment.buyer_id === user.id`.
 *
 * THREE THINGS FIXED IN PASSING
 * -----------------------------
 * 1. `showToast.error(res.error)` passed the API's error OBJECT to a toast,
 *    which renders as nothing useful. `res.error` on a failed call is the
 *    response body, so the string is dug out of it here.
 * 2. The only failure `track_order` can produce is "no shipment found", and it
 *    arrives as a 400. That is an empty RESULT, not an error, so it sets
 *    `notFound` and the view draws an `Empty` rather than shouting.
 * 3. The empty-input guard toasted from the corner of the screen. It sets a
 *    field error now, beside the field that caused it.
 */

/**
 * True for a value that is safe to put in front of a user.
 *
 * A failed call does not always carry a sentence. When the API is down the
 * dev proxy answers with a whole HTML error page, and the pre-conversion code
 * would have handed that straight to a toast. Anything markup-shaped or longer
 * than a sentence is rejected in favour of the fallback.
 */
function isSentence(value) {
  if (typeof value !== 'string') return false;
  const text = value.trim();
  return Boolean(text) && text.length <= 160 && !text.includes('<');
}

/** Dig a displayable sentence out of whatever the module handed back. */
function messageFrom(res, fallback) {
  if (!res) return fallback;
  const body = res.error;
  if (isSentence(body)) return body.trim();
  if (body && isSentence(body.error)) return body.error.trim();
  if (body && isSentence(body.message)) return body.message.trim();
  if (isSentence(res.message)) return res.message.trim();
  return fallback;
}

/** True when the failure is simply "that ID is not one of ours". */
function isNotFound(res) {
  return /no shipment found/i.test(messageFrom(res, ''));
}

export function useOrderTracking({ mutationKey = 'track-order' } = {}) {
  const location = useLocation();
  const { user } = useAuth();

  const [trackingId, setTrackingId] = useState(() => location?.state?.trackingId || '');
  const [shipment, setShipment] = useState(null);
  const [fieldError, setFieldError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [review, setReview] = useState({ rating: 0, review: '' });
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  const [disputeOpen, setDisputeOpen] = useState(false);
  const [dispute, setDispute] = useState({ reason: '', description: '', evidencePreview: null });
  const [disputeSubmitted, setDisputeSubmitted] = useState(false);

  const trackOrderMutation = useMutation({
    mutationKey: [mutationKey],
    mutationFn: async ({ tracking_number }) => orderTrackingModule.trackingOrder({ tracking_number }),
    onSuccess: (res) => {
      if (res.success) {
        setShipment(res.data?.results || null);
        setNotFound(false);
        setError(null);
        return;
      }
      setShipment(null);
      if (isNotFound(res)) {
        setNotFound(true);
        setError(null);
        return;
      }
      setNotFound(false);
      setError(messageFrom(res, 'We could not track that order. Try again in a moment.'));
    },
    onError: () => {
      setShipment(null);
      setNotFound(false);
      setError('We could not track that order. Try again in a moment.');
    },
  });

  const submitRatingMutation = useMutation({
    mutationKey: ['submit-rating'],
    mutationFn: async (payload) => orderTrackingModule.submitRating(payload),
    onSuccess: (res) => {
      if (res.success) {
        showToast.success(res.message || 'Submitted');
        setReviewSubmitted(true);
        setReviewOpen(false);
      } else {
        showToast.error(res.message || 'Failed to submit rating');
      }
    },
    onError: () => showToast.error('Failed to submit rating'),
  });

  const handleSubmit = useCallback(
    (value) => {
      const id = String(value || '').trim();
      if (!id) {
        setFieldError('Enter a tracking ID to continue.');
        return;
      }
      setFieldError(null);
      trackOrderMutation.mutate({ tracking_number: id });
    },
    [trackOrderMutation],
  );

  const handleTrackingIdChange = useCallback((value) => {
    setTrackingId(value);
    setFieldError(null);
    setNotFound(false);
    setError(null);
  }, []);

  const handleReset = useCallback(() => {
    setShipment(null);
    setNotFound(false);
    setError(null);
    setTrackingId('');
  }, []);

  const handleReviewSubmit = useCallback(
    ({ rating, review: text }) => {
      submitRatingMutation.mutate({
        rating: Number(rating) || 5,
        review: String(text || '').trim(),
        order_id: shipment?.orderId || shipment?.order_id || '',
      });
    },
    [shipment, submitRatingMutation],
  );

  // The dispute endpoint does not exist yet: `orderTracking.module.js` has no
  // dispute method and the pre-conversion form's `onSubmit` was a literal
  // `() => {}`. The dialog is wired end to end so the day it lands only this
  // callback changes, and until then it closes honestly rather than pretending
  // to have filed something.
  const handleDisputeSubmit = useCallback(() => {
    setDisputeSubmitted(true);
    setDisputeOpen(false);
    showToast.info('Dispute noted. Our team will be in touch about this order.');
  }, []);

  const handleDisputeChange = useCallback((next) => {
    // The evidence picker hands back the change event, not a URL, so the object
    // URL is minted here rather than inside a pure form body.
    if (next.evidenceEvent) {
      const file = next.evidenceEvent.target?.files?.[0];
      const { evidenceEvent, ...rest } = next;
      setDispute({
        ...rest,
        evidencePreview: file ? URL.createObjectURL(file) : rest.evidencePreview || null,
      });
      return;
    }
    setDispute(next);
  }, []);

  /**
   * The actions gate, unchanged from both pre-conversion pages:
   * `shipmentData?.buyer_id === user?.id`.
   *
   * KNOWN APP BUG, NOT MASKED HERE. `track_order` returns only
   * `{tracking_number, courier, status, last_update}`, so `buyer_id` is always
   * `undefined` and this is always false. Review and dispute are therefore
   * unreachable on both routes today. The gate is correct and the payload is
   * short; the fix belongs on the endpoint, not on this page.
   */
  const canAct = useMemo(() => {
    const buyerId = shipment?.buyer_id;
    return Boolean(buyerId && user?.id && buyerId === user.id && isSettled(shipment?.status));
  }, [shipment, user]);

  return {
    trackingId,
    onTrackingIdChange: handleTrackingIdChange,
    onSubmit: handleSubmit,
    loading: trackOrderMutation.isPending,
    fieldError,
    notFound,
    error,
    shipment,
    onReset: handleReset,
    canAct,

    reviewOpen,
    onReviewOpen: () => setReviewOpen(true),
    onReviewClose: () => setReviewOpen(false),
    review,
    onReviewChange: setReview,
    onReviewSubmit: handleReviewSubmit,
    reviewLoading: submitRatingMutation.isPending,
    reviewSubmitted,

    disputeOpen,
    onDisputeOpen: () => setDisputeOpen(true),
    onDisputeClose: () => setDisputeOpen(false),
    dispute,
    onDisputeChange: handleDisputeChange,
    onDisputeSubmit: handleDisputeSubmit,
    disputeLoading: false,
    disputeSubmitted,
  };
}

export default useOrderTracking;
